<script>
// App-bar status for offline work: a chip counting the offline changes waiting
// to sync (red once any of them failed or conflicted), or, with none waiting,
// a chip counting the locations downloaded for offline use, plus an Offline
// chip while the browser has no connection. Clicking the count opens the one
// location this browser holds work or a download for, in its section; with
// several, it opens the Offline list, where the work syncs or gets inspected
// per item. The app bar has no Infrared or LOTO entry (2026-10-27), so the
// downloads chip is the way to a downloaded location, with or without a
// connection (a weak one can still report itself online).
//
// Recounts are event-driven (the queues and the downloads ping offline-events
// on every stored change). No polling.
import { allItems as outboxItems } from '@src/graphql/outbox'
import { allUploads } from '@src/graphql/upload-queue'
import { subscribeOfflineQueues } from '@src/graphql/offline-events'
import { createOfflineStore } from '@src/graphql/offline-store'
import { reportError } from '@utils/report-error'

// The downloads' index (a small record per location), not the feature modules
// (infrared-offline, loto-offline: big, with their queries), so the app bar can
// count them as it loads.
const downloads = {
  infrared: createOfflineStore('infrared'),
  loto: createOfflineStore('loto'),
}

export default {
  name: 'OfflinePendingChip',
  data() {
    return {
      pending: 0,
      failed: 0,
      downloaded: 0,
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
      unsubscribe: null,
    }
  },
  computed: {
    // The downloads count for whoever may work them offline (a browser
    // someone else downloaded on shows nothing to a customer).
    canWorkOffline() {
      return this.mayWork('infrared') || this.mayWork('loto')
    },
  },
  mounted() {
    this.recount()
    this.unsubscribe = subscribeOfflineQueues(this.recount)
    window.addEventListener('online', this.onConnectivity)
    window.addEventListener('offline', this.onConnectivity)
  },
  beforeUnmount() {
    if (this.unsubscribe) this.unsubscribe()
    window.removeEventListener('online', this.onConnectivity)
    window.removeEventListener('offline', this.onConnectivity)
  },
  methods: {
    onConnectivity() {
      this.online = navigator.onLine
    },
    // Whether the user may work the feature offline (infrared.offline,
    // loto.offline).
    mayWork(feature) {
      const can = this.$store && this.$store.getters['auth/can']
      return Boolean(can && can(`${feature}.offline`))
    },
    async recount() {
      try {
        const [items, uploads, downloaded] = await Promise.all([
          outboxItems(),
          allUploads(),
          this.countDownloads(),
        ])
        this.pending = items.length + uploads.length
        this.failed =
          items.filter(
            (item) => item.status === 'failed' || item.status === 'conflict'
          ).length + uploads.filter((job) => job.status === 'failed').length
        this.downloaded = downloaded
      } catch (err) {
        reportError(err, { source: 'offline-pending-chip' })
      }
    },
    // The locations downloaded for the features the user may work offline,
    // each once (Infrared and LOTO can both hold one).
    async countDownloads() {
      const held = await Promise.all(
        ['infrared', 'loto'].map((feature) =>
          this.mayWork(feature) ? downloads[feature].listLocations() : []
        )
      )
      return new Set(held.flat().map((record) => String(record.locationId)))
        .size
    },
    async open() {
      const target = await this.offlineTarget().catch(() => null)
      this.$router
        .push(target || { path: '/infrared', query: { tab: 'offline' } })
        .catch(() => {})
    },
    // The single location the queued work belongs to (Infrared, or LOTO for
    // the people LOTO admits), as the devices page takes it, opened on that
    // section's Offline tab. Null when the work spans several locations: the
    // Offline list then lists them all.
    //
    // THE WORK DECIDES, NOT THE DOWNLOADS. The chip counts queued changes, and
    // a click used to land on whichever location held a download. Those are
    // not always the same place: work saved under the wrong site's download,
    // or work whose download is gone, sent the tech to a page that showed
    // none of the changes the chip was counting. Only with no work tied to
    // a location do the downloads decide, as before.
    async offlineTarget() {
      // LOTO's offline work is for those who may work LOTO offline.
      const lotoOffline = this.mayWork('loto')
      const [infrared, loto, items, uploads] = await Promise.all([
        downloads.infrared.listLocations(),
        lotoOffline ? downloads.loto.listLocations() : [],
        outboxItems(),
        allUploads(),
      ])
      // One place per location (ids compared as strings, see
      // outbox.matchesLocation), Infrared preferred when both features hold it.
      const add = (places, locationId, section) => {
        const key = String(locationId)
        const known = places.get(key)
        if (!known || (known.section === 'loto' && section === 'infrared')) {
          places.set(key, { locationId, section })
        }
      }
      let places = new Map()
      for (const entry of [...items, ...uploads]) {
        if (entry.locationId == null) continue
        // Untagged work is Infrared's (see outbox.matchesFeature).
        const section = entry.feature === 'loto' ? 'loto' : 'infrared'
        if (section === 'loto' && !lotoOffline) continue
        add(places, entry.locationId, section)
      }
      if (!places.size) {
        places = new Map()
        for (const record of infrared)
          add(places, record.locationId, 'infrared')
        for (const record of loto) add(places, record.locationId, 'loto')
      }
      if (places.size !== 1) return null
      const [place] = places.values()
      const key = String(place.locationId)
      const record = [...infrared, ...loto].find(
        (r) => String(r.locationId) === key && r.name
      )
      return {
        name: 'devices',
        query: {
          locationId: key,
          locationName: record ? record.name : '',
          section: place.section,
          offline: place.section,
        },
      }
    },
  },
}
</script>

<template>
  <div class="d-flex align-center">
    <v-chip
      v-if="!online"
      color="white"
      variant="outlined"
      size="small"
      class="mr-2 font-weight-medium"
      data-test="offline-chip"
    >
      {{ $t('infrared.offline.indicatorOffline') }}
    </v-chip>
    <v-tooltip
      v-if="pending"
      :text="$t('infrared.offline.indicatorPending', { count: pending })"
      location="bottom"
    >
      <template v-slot:activator="{ props }">
        <v-chip
          v-bind="props"
          :color="failed ? 'yellow-lighten-3' : 'white'"
          variant="outlined"
          size="small"
          class="mr-2 font-weight-medium"
          prepend-icon="$download"
          data-test="pending-chip"
          @click="open"
        >
          {{ pending }}
        </v-chip>
      </template>
    </v-tooltip>
    <!-- Nothing waiting: the downloaded locations, the way to them. -->
    <v-tooltip
      v-else-if="downloaded && canWorkOffline"
      :text="
        $t(
          'infrared.offline.indicatorDownloaded',
          { count: downloaded },
          downloaded
        )
      "
      location="bottom"
    >
      <template v-slot:activator="{ props }">
        <v-chip
          v-bind="props"
          color="white"
          variant="outlined"
          size="small"
          class="mr-2 font-weight-medium"
          prepend-icon="$downloaded"
          data-test="downloads-chip"
          @click="open"
        >
          {{ downloaded }}
        </v-chip>
      </template>
    </v-tooltip>
  </div>
</template>
